import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Star, Tag } from 'lucide-react';
import './Store.css';
import storeVinyl from '../assets/images/store-vinyl.svg';
import storeShirt from '../assets/images/store-shirt.svg';
import storeGuitarPick from '../assets/images/store-guitar-pick.svg';
import storePoster from '../assets/images/store-poster.svg';
import storeCap from '../assets/images/store-cap.svg';
import usePageTitle from '../hooks/usePageTitle';

const Store: React.FC = () => {
  usePageTitle('Store - The Kults');
  const navigate = useNavigate();
  const [cart, setCart] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'vinyl', name: 'Vinyl Records' },
    { id: 'clothing', name: 'Clothing' },
    { id: 'accessories', name: 'Accessories' },
    { id: 'collectibles', name: 'Collectibles' }
  ];

  const products = [
    {
      id: 1,
      name: 'Elephant Vinyl LP',
      category: 'vinyl',
      price: 29.99,
      originalPrice: 34.99,
      image: storeVinyl,
      description: 'Limited edition red vinyl pressing of the iconic album',
      rating: 5,
      reviews: 128,
      inStock: true,
      featured: true
    },
    {
      id: 2,
      name: 'White Blood Cells Vinyl LP',
      category: 'vinyl',
      price: 24.99,
      originalPrice: null,
      image: storeVinyl,
      description: 'Classic white vinyl pressing',
      rating: 5,
      reviews: 95,
      inStock: true,
      featured: false
    },
    {
      id: 3,
      name: 'The Kults T-Shirt',
      category: 'clothing',
      price: 24.99,
      originalPrice: null,
      image: storeShirt,
      description: 'Classic band logo t-shirt in black',
      rating: 4,
      reviews: 67,
      inStock: true,
      featured: false
    },
    {
      id: 4,
      name: 'Seven Nation Army Hoodie',
      category: 'clothing',
      price: 49.99,
      originalPrice: 59.99,
      image: storeShirt,
      description: 'Comfortable hoodie featuring the iconic song title',
      rating: 5,
      reviews: 42,
      inStock: true,
      featured: true
    },
    {
      id: 5,
      name: 'Guitar Pick Set',
      category: 'accessories',
      price: 9.99,
      originalPrice: null,
      image: storeGuitarPick,
      description: 'Set of 5 custom White Stripes guitar picks',
      rating: 4,
      reviews: 23,
      inStock: true,
      featured: false
    },
    {
      id: 6,
      name: 'Limited Edition Poster',
      category: 'collectibles',
      price: 19.99,
      originalPrice: null,
      image: storePoster,
      description: 'Exclusive concert poster from 2003 tour',
      rating: 5,
      reviews: 31,
      inStock: false,
      featured: false
    },
    {
      id: 7,
      name: 'Get Behind Me Satan Vinyl LP',
      category: 'vinyl',
      price: 27.99,
      originalPrice: null,
      image: storeVinyl,
      description: 'Experimental masterpiece on vinyl',
      rating: 4,
      reviews: 78,
      inStock: true,
      featured: false
    },
    {
      id: 8,
      name: 'Band Logo Cap',
      category: 'accessories',
      price: 19.99,
      originalPrice: null,
      image: storeCap,
      description: 'Embroidered band logo baseball cap',
      rating: 4,
      reviews: 15,
      inStock: true,
      featured: false
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  const addToCart = (productId: number) => {
    setCart([...cart, productId]);
  };

  const removeFromCart = (productId: number) => {
    setCart(cart.filter(id => id !== productId));
  };

  const isInCart = (productId: number) => cart.includes(productId);

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="store-page">
      <section className="store-hero">
        <div className="container">
          <h1>STORE</h1>
          <p>Official merchandise and music from The Kults</p>
        </div>
      </section>

      <section className="store-content">
        <div className="container">
          <div className="store-header">
            <div className="store-filters">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <Tag size={16} />
                  {category.name}
                </button>
              ))}
            </div>
            <div className="cart-summary">
              <ShoppingCart size={20} />
              <span>{cart.length} items</span>
            </div>
          </div>

          <div className="products-grid">
            {filteredProducts.map((product) => (
              <div 
                key={product.id} 
                className={`product-card ${product.featured ? 'featured' : ''}`}
                onClick={() => handleProductClick(product.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleProductClick(product.id);
                  }
                }}
              >
                <div className="product-image">
                  <img src={product.image} alt={product.name} />
                  {product.featured && (
                    <div className="featured-badge">Featured</div>
                  )}
                  {!product.inStock && (
                    <div className="out-of-stock">Out of Stock</div>
                  )}
                  <button className="wishlist-btn">
                    <Heart size={20} />
                  </button>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < product.rating ? 'filled' : 'empty'}
                      />
                    ))}
                    <span className="review-count">({product.reviews})</span>
                  </div>
                  <div className="product-price">
                    <span className="current-price">${product.price}</span>
                    {product.originalPrice && (
                      <span className="original-price">${product.originalPrice}</span>
                    )}
                  </div>
                  <button
                    className={`cart-btn ${isInCart(product.id) ? 'in-cart' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      isInCart(product.id) 
                        ? removeFromCart(product.id) 
                        : addToCart(product.id);
                    }}
                    disabled={!product.inStock}
                  >
                    <ShoppingCart size={16} />
                    {isInCart(product.id) ? 'Remove from Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Store; 